import { ICommand } from 'core-ts-types';
import * as _4 from 'lodash4';
import { NavigationHistoryStack } from 'ui-ng-navbar';
import { IPopupConfigService } from 'ui-ng-popup';
import { PlatformUtility } from 'utility-ts-platform';
import { StringUtility, TimeUtility } from 'utility-ts-type';
import { ConversationThread } from '../../../src/builders/conversation_thread_builder';
import { IConversationCardModel } from '../../../src/conversations_interface';
import { ConversationThreadsQuery } from '../../queries/conversation_threads_query';
import { ConversationsQuery } from '../../queries/conversations_query';
import { ConversationsCapabilityConfiguration } from '../../configuration';

interface IConversationThreadCardFlags {
  isUnread: boolean;
  isFlagged: boolean;
  hasMobileNotes: boolean;
  hasDesktopNotes: boolean;
  showActionLink: boolean;
}

export class ConversationThreadCardController {
  private conversationThread: ConversationThread;
  private model: IConversationCardModel;
  private flags: IConversationThreadCardFlags;
  private reviewLinkText: string = 'Resolve Suggestion(s)';
  constructor(
    private ngConversationThreadsQuery: ConversationThreadsQuery,
    private ngConversationsQuery: ConversationsQuery,
    private deleteConversationThreadCommand: ICommand,
    private updateConversationThreadStatusCommand: ICommand,
    private ConversationThreadStatusEnum: any,
    private ngNavigationHistoryStack: NavigationHistoryStack,
    private ngPopupService: IPopupConfigService,
    private $state: ng.ui.IStateService,
    private $stateParams: ng.ui.IStateParamsService,
    private ngConversationsCapabilityConfiguration: ConversationsCapabilityConfiguration
  ) {
    this.deleteThread = this.deleteThread.bind(this);
  }

  $onInit(): void {
    this.__setReviewLinkText();
    this.flags = {
      hasMobileNotes:
        !PlatformUtility.isDesktop() &&
        !_4.isEmpty(_4.get(this.conversationThread, 'source_properties.mobile_postfix', '')),
      hasDesktopNotes:
        PlatformUtility.isDesktop() &&
        !_4.isEmpty(_4.get(this.conversationThread, 'source_properties.desktop_postfix', '')),
      showActionLink:
        !_4.isEmpty(this.conversationThread.callback_url) &&
        (this.isAPNotification() ? PlatformUtility.isDesktop() : true),
      isUnread: this.ConversationThreadStatusEnum.isUnread(this.conversationThread.status),
      isFlagged: this.ConversationThreadStatusEnum.isFlagged(this.conversationThread.status)
    };

    this.model = {
      conversationDate: TimeUtility.formatMonthDayYear(this.conversationThread.sent_timestamp),
      initials: this.isAPNotification()
        ? StringUtility.getAcronym(...this.conversationThread.friendly_sender_name.split(' '))
        : 'NS',
      email_message: this.conversationThread.email_message
        ? this.conversationThread.email_message.replace(/\n/g, '<br />')
        : ''
    };
  }

  $onChanges() {
    this.__setReviewLinkText();
  }

  __setReviewLinkText() {
    const defaultText = 'Resolve Suggestion(s)';
    this.reviewLinkText = _4.get(this.conversationThread, 'ui_action_text', defaultText);
    if (_4.isEmpty(this.reviewLinkText)) {
      this.reviewLinkText = defaultText;
    }
  }

  isAPNotification(): boolean {
    return this.conversationThread.source_name === 'AP';
  }

  isMobile(): boolean {
    return PlatformUtility.isMobile();
  }

  goToActionButtonState(): void {
    if (this.conversationThread.callback_url.includes('/dl/study')) {
      if (this.ngConversationsCapabilityConfiguration.config.isNeiEnabled) {
        window.location.assign('#/my/native/features/study/sections');
      } else {
        this.$state.go('my.studyGroups');
      }
    }
    if (this.conversationThread.callback_url.includes('/dl/holdslist')) {
      if (this.ngConversationsCapabilityConfiguration.config.isNeiEnabled) {
        window.location.assign('#/my/native/features/features/holds');
      } else {
        this.$state.go('my.holdscenter');
      }
    }
    if (this.conversationThread.callback_url.includes('/dl/surveys')) {
      const survey_id = _4.get(this.conversationThread, 'source_properties.survey_id', 0);

      if (this.ngConversationsCapabilityConfiguration.config.isNeiEnabled) {
        window.location.assign(`#/my/native/features/surveys/intro/${survey_id}`);
      } else {
        this.$state.go('my.survey-intro', { surveyID: survey_id });
      }
    }
    if (this.conversationThread.callback_url.includes('/dl/event')) {
      this.$state.go('my.todos.details', {
        eventId: _4.get(this.conversationThread, 'source_properties.event', 0),
        repeatedBy: _4.get(this.conversationThread, 'source_properties.repeated_by', 0)
      });
    }
    if (this.conversationThread.callback_url.includes('/dl/ptd')) {
      this.$state.go('my.todos.reminder', {
        personalTodoID: _4.get(this.conversationThread, 'source_properties.personaltodo_id', 0)
      });
    }
    // Journeys feature is a micro app and not part of angular app, so we are not using angular routing
    if (this.conversationThread.callback_url.includes('/dl/journeys_journey')) {
      const journey_id = _4.get(this.conversationThread, 'source_properties.journey_id', 0);
      const journeysRouteinNei = `#/my/native/features/event/journey/${journey_id}/review`;

      return window.location.assign(journeysRouteinNei);
    }
    if (this.conversationThread.callback_url.includes('/dl/journeys_step')) {
      const step_id = _4.get(this.conversationThread, 'source_properties.step_id', 0);
      const journeyStepRouteinNei = `#/my/native/features/checklist/item/Step/${step_id}`;

      return window.location.assign(journeyStepRouteinNei);
    }
    if (this.conversationThread.callback_url.includes('/dl/pre_appointment_questions/')) {
      const token = _4.get(this.conversationThread, 'source_properties.token', '');
      const preAppointmentUrl = `#/my/host/as3/pre-appointment-questions?token=${token}`;

      return window.location.assign(preAppointmentUrl);
    }
    if (this.conversationThread.callback_url.includes('/dl/post_appointment_feedback/')) {
      const token = _4.get(this.conversationThread, 'source_properties.token', '');
      const postAppointmentUrl = `#/my/host/as3/appointment-feedback?token=${token}`;

      return window.location.assign(postAppointmentUrl);
    }
  }

  deleteThread(): void {
    this.deleteConversationThreadCommand(this.conversationThread).then(() => {
      if (_4.isEmpty(this.ngConversationThreadsQuery.result)) {
        this.ngNavigationHistoryStack.goBack();
      }
    });
  }

  deleteThreadAlert(): void {
    this.ngPopupService.show({
      title: 'Delete Notification',
      body: 'Are you sure you want to delete this notification? This cannot be undone.',
      completeButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      completeCallback: this.deleteThread,
      cancelCallback: angular.noop
    });
  }

  toggleFlaggedStatus(): void {
    this.updateConversationThreadStatusCommand(this.conversationThread).then(() => {
      this.flags.isFlagged = this.ConversationThreadStatusEnum.isFlagged(this.conversationThread.status);
      const conversation = _4.find(
        this.ngConversationsQuery.result.conversations,
        (conv) => conv.id === this.$stateParams.id
      );
      if (!_4.isEmpty(conversation)) {
        conversation.flagged = this.flags.isFlagged ? ++conversation.flagged : --conversation.flagged;
      }
    });
  }
}

export class ConversationThreadCardComponent implements angular.IComponentOptions {
  public templateUrl: string =
    'capability-ng-conversations/src/components/conversation_thread_card/conversation_thread_card.html';
  public controller: any = ConversationThreadCardController;
  public controllerAs: string = '$ctrl';
  public bindings: any = {
    conversationThread: '<'
  };
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-conversations/src/components/conversation_thread_card/conversation_thread_card_controller.ts